import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ContentProvider } from "./context/ContentContext";
import { ActiveStoreProvider } from "./context/ActiveStoreContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import ScrollToTop from "./components/ScrollToTop";
import { MARKA_OLDALAK } from "./constants/markaOldalak";
import { MARKA_SZERVIZ } from "./constants/markaSzerviz";
import { MARKA_FELVASARLAS } from "./constants/markaFelvasarlas";
import SEOAliasRoute from "./components/SEOAliasRoute";
import FloatingContactWidget from "./components/FloatingContactWidget";
import MobileCTABar from "./components/MobileCTABar";
import ErrorBoundary from "./components/ErrorBoundary";
import { initScrollDepthTracking } from "./utils/analytics";

// ── Eager loaded – KIZÁRÓLAG a főoldal (LCP-kritikus landing) ─────────────────
import HomePage from "./pages/HomePage";

// ── Lazy loaded – minden más oldal ────────────────────────────────────────────
// Perf Sprint #1: ServicePage/HydrogelPage/BuybackPage is lazy → a main.js csak
// a főoldalt tartalmazza. Minden oldal prerenderelt, így a SEO-t nem érinti;
// a Suspense fallback (PageLoader) útvonalváltáskor rövid, semleges töltő.
// A popup késleltetve jelenik meg → a kódja se terhelje az első betöltést.
const EmailPopup              = lazy(() => import("./components/EmailPopup"));
const ServicePage             = lazy(() => import("./pages/ServicePage"));
const HydrogelPage            = lazy(() => import("./pages/HydrogelPage"));
const BuybackPage             = lazy(() => import("./pages/BuybackPage"));
const PhonesPage              = lazy(() => import("./pages/PhonesPage"));
const StoresPage              = lazy(() => import("./pages/StoresPage"));
const VipClubPage             = lazy(() => import("./pages/VipClubPage"));
const VipRulesPage            = lazy(() => import("./pages/VipRulesPage"));
const BlogPage                = lazy(() => import("./pages/BlogPage"));
const BlogPostPage            = lazy(() => import("./pages/BlogPostPage"));
const PhoneDetailPage         = lazy(() => import("./pages/PhoneDetailPage"));
const FaqPage                 = lazy(() => import("./pages/FaqPage"));
const MiertNorbiPhonePage     = lazy(() => import("./pages/MiertNorbiPhonePage"));
const ReviewRequestPage       = lazy(() => import("./pages/ReviewRequestPage"));
const GBPHelperPage           = lazy(() => import("./pages/GBPHelperPage"));
const IphoneKijelzocserePage  = lazy(() => import("./pages/IphoneKijelzocserePage"));
const IphoneHatlapcserePage   = lazy(() => import("./pages/IphoneHatlapcserePage"));
const IphoneAkkumulatorcserePage = lazy(() => import("./pages/IphoneAkkumulatorcserePage"));
const SamsungKijelzocserePage = lazy(() => import("./pages/SamsungKijelzocserePage"));
const SamsungXiaomiAkkumulatorcserePage = lazy(() => import("./pages/SamsungXiaomiAkkumulatorcserePage"));
const PrivacyPage        = lazy(() => import("./pages/PrivacyPage"));
const CookiePage         = lazy(() => import("./pages/CookiePage"));
const TermsPage          = lazy(() => import("./pages/TermsPage"));
const ImpresszumPage     = lazy(() => import("./pages/ImpresszumPage"));
const AkadalymentessegPage = lazy(() => import("./pages/AkadalymentessegPage"));
const EszkozTesztPage = lazy(() => import("./pages/EszkozTesztPage"));
const MarkaSzervizPage = lazy(() => import("./pages/MarkaSzervizPage"));
const MarkaFelvasarlasPage = lazy(() => import("./pages/MarkaFelvasarlasPage"));
const BejelentesPage = lazy(() => import("./pages/BejelentesPage"));
const NyitvaPage = lazy(() => import("./pages/NyitvaPage"));
const HivtalPage = lazy(() => import("./pages/HivtalPage"));
const TelefonvadaszPage = lazy(() => import("./pages/TelefonvadaszPage"));
const BejelentesAdatkezelesPage = lazy(() => import("./pages/BejelentesAdatkezelesPage"));
const StolenReportsAdminPage = lazy(() => import("./pages/StolenReportsAdminPage"));
const PhoneHunterAdminPage = lazy(() => import("./pages/PhoneHunterAdminPage"));
const ChallengePage      = lazy(() => import("./pages/ChallengePage"));

// ── Lazy loaded – admin (csak belső használat, nem SEO) ───────────────────────
const NotFoundPage       = lazy(() => import("./pages/NotFoundPage"));
// Univerzális, adatvezérelt összehasonlító rendszer (P5) – a devices.mjs
// bővítésével automatikusan új oldalak jönnek létre, új route/komponens nélkül.
const CompareIndexPage   = lazy(() => import("./pages/CompareIndexPage"));
const ComparePage        = lazy(() => import("./pages/ComparePage"));

const AdminPage          = lazy(() => import("./pages/AdminPage"));
const PhonesAdminPage    = lazy(() => import("./pages/PhonesAdminPage"));
const NewsletterAdminPage = lazy(() => import("./pages/NewsletterAdminPage"));
const BuybackLeadsPage   = lazy(() => import("./pages/BuybackLeadsPage"));
const LeadCenterPage     = lazy(() => import("./pages/LeadCenterPage"));
const CouponsAdminPage   = lazy(() => import("./pages/CouponsAdminPage"));
// Rejtett (noindex, sehonnan nem linkelt) publikus kupon-státusz oldal – a
// kolléga bejelentkezés nélkül lekérdezheti egy kód állapotát (csak olvasás).
const KuponStatusPage    = lazy(() => import("./pages/KuponStatusPage"));

// ── Loader fallback ───────────────────────────────────────────────────────────
// A minHeight SZÁNDÉKOSAN 100vh (korábban 60vh volt). Ok: minden oldal
// prerenderelt, de a route-komponensek lazy-k – a createRoot() az első
// rendernél lecseréli a kisült HTML-t erre a fallbackre, amíg a chunk meg nem
// érkezik. 60vh mellett a lábléc BELECSÚSZOTT a képernyőbe, majd a valódi
// tartalom megjelenésekor visszaugrott: mért CLS 0,4005 (Core Web Vitals:
// „gyenge”) minden oldalon a főoldalon kívül (a HomePage nem lazy).
// 100vh-val a lábléc nem fér be, így nincs elmozdulás a látómezőben.
// A fallback idejét külön a chunk-preload csökkenti (scripts/prerender.js).
const PageLoader = () => (
  <div style={{
    display: "flex", alignItems: "center", justifyContent: "center",
    minHeight: "100vh"
  }}>
    <div style={{
      width: "28px", height: "28px", border: "3px solid #e5e7eb",
      borderTop: "3px solid var(--brand)", borderRadius: "50%",
      animation: "spin 0.8s linear infinite"
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function App() {
  // Scroll-mélység mérés (GA4 scroll_depth, 25/50/75/100%) – egyetlen globális,
  // passive listener az egész appra.
  useEffect(() => initScrollDepthTracking(), []);

  return (
    <div className="App">
      <ContentProvider>
        {/* Az aktív készülék boltja – a sticky mobil sáv innen tudja meg, hogy
            a látogató épp melyik üzlet darabját nézi (ld. ActiveStoreContext). */}
        <ActiveStoreProvider>
        <BrowserRouter>
          <ScrollToTop />
          <a href="#fo-tartalom" className="skip-link">Ugrás a tartalomhoz</a>
          <Header />
          {/* tabIndex={-1}: a skip-link célja megbízhatóan kapjon fókuszt minden
              böngésző/AT párosnál – enélkül a fókusz a linken ragadhat. */}
          <main id="fo-tartalom" tabIndex={-1} className="scroll-mt-24 outline-none">
            <ErrorBoundary name="routes">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/"            element={<HomePage />} />
                <Route path="/szerviz"     element={<ServicePage />} />
                <Route path="/hidrogel"    element={<HydrogelPage />} />
                <Route path="/telefonok"   element={<PhonesPage />} />
                <Route path="/felvasarlas" element={<BuybackPage />} />
                <Route path="/uzletek"     element={<StoresPage />} />
                <Route path="/vip-klub"         element={<VipClubPage />} />
                <Route path="/vip-jatekszabalyzat" element={<VipRulesPage />} />
                <Route path="/blog"             element={<BlogPage />} />
                <Route path="/blog/:slug"       element={<BlogPostPage />} />
                <Route path="/telefonok/:uid"   element={<PhoneDetailPage />} />
                {/* Bolt mód: szerviz-mentes változat hirdetési platformokhoz (utils/boltMod.js) */}
                <Route path="/bolt"             element={<Navigate to="/bolt/telefonok/" replace />} />
                <Route path="/bolt/telefonok"   element={<PhonesPage />} />
                <Route path="/bolt/telefonok/:uid" element={<PhoneDetailPage />} />
                <Route path="/bolt/felvasarlas" element={<BuybackPage />} />
                <Route path="/bolt/uzletek"     element={<StoresPage />} />
                <Route path="/gyakori-kerdesek" element={<FaqPage />} />
                <Route path="/miert-norbiphone" element={<MiertNorbiPhonePage />} />
                <Route path="/ertekeles" element={<ReviewRequestPage />} />
                <Route path="/admin/gbp-helper" element={<GBPHelperPage />} />
                <Route path="/iphone-kijelzocsere-oroshaza"    element={<IphoneKijelzocserePage />} />
                <Route path="/iphone-hatlapcsere"              element={<IphoneHatlapcserePage />} />
                <Route path="/iphone-akkumulatorcsere-oroshaza" element={<IphoneAkkumulatorcserePage />} />
                <Route path="/samsung-kijelzocsere-oroshaza"   element={<SamsungKijelzocserePage />} />
                <Route path="/samsung-xiaomi-akkumulatorcsere" element={<SamsungXiaomiAkkumulatorcserePage />} />

                <Route path="/telefon-felvasarlas-oroshaza"     element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />
                <Route path="/hasznalt-telefon-eladas-oroshaza" element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />
                <Route path="/telefon-becsere-oroshaza"         element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />

                <Route path="/vip-pult"    element={<Navigate to="/vip-klub/?source=pult"    replace />} />
                <Route path="/vip-kirakat" element={<Navigate to="/vip-klub/?source=kirakat" replace />} />
                <Route path="/vip-folia"   element={<Navigate to="/vip-klub/?source=folia"   replace />} />

                {/* Régi, megszűnt URL-ek átirányítása (GSC-ben még indexeltek) */}
                <Route path="/about" element={<Navigate to="/miert-norbiphone/" replace />} />
                <Route path="/fixes" element={<Navigate to="/szerviz/" replace />} />

                {/* Z10: elgépelt SEO-slugok → helyes írásmód (a régi, esetleg indexelt URL nem 404-el) */}
                <Route path="/betort-kijlzo-javitas-oroshaza"     element={<Navigate to="/betort-kijelzo-javitas-oroshaza" replace />} />
                <Route path="/telefon-kijlzovedo-folia-oroshaza"  element={<Navigate to="/telefon-kijelzovedo-folia-oroshaza" replace />} />
                <Route path="/telefon-akkumulatora-gyorsan-lemeruel-oroshaza" element={<Navigate to="/telefon-akkumulatora-gyorsan-lemerul-oroshaza" replace />} />

                <Route path="/adatkezeles" element={<PrivacyPage />} />
                <Route path="/cookie"      element={<CookiePage />} />
                <Route path="/aszf"        element={<TermsPage />} />
                <Route path="/impresszum"  element={<ImpresszumPage />} />
                <Route path="/akadalymentesseg" element={<AkadalymentessegPage />} />
                <Route path="/eszkozteszt" element={<EszkozTesztPage />} />
                <Route path="/bejelentes" element={<BejelentesPage />} />
                {/* SMS-ből nyíló landing nem fogadott hívás után – noindex,
                    fejléc/lábléc nélkül (ld. constants/ctaMentesOldalak.js). */}
                <Route path="/nyitva" element={<NyitvaPage />} />
                {/* Nem fogadott hívás utáni SMS célja – noindex, csupasz. */}
                <Route path="/hivtal" element={<HivtalPage />} />
                <Route path="/telefonvadasz" element={<TelefonvadaszPage />} />
                <Route path="/bejelentes-adatkezeles" element={<BejelentesAdatkezelesPage />} />
                <Route path="/challenge"   element={<ChallengePage />} />

                {/* Rejtett, noindex kupon-státusz oldal (nincs sehol linkelve,
                    nem prerenderelt) – pultos gyors ellenőrzés login nélkül. */}
                <Route path="/kuponstatus" element={<KuponStatusPage />} />

                <Route path="/admin"                        element={<AdminPage />} />
                <Route path="/admin/telefonok"              element={<PhonesAdminPage />} />
                <Route path="/admin/hirlevel"               element={<NewsletterAdminPage />} />
                <Route path="/admin/felvasarlas-leadek"     element={<BuybackLeadsPage />} />
                {/* Sprint 2C: EGY admin belépési pont a leadekhez. A régi
                    /admin/foglalasok bookmarkok a Lead Centerbe futnak. */}
                <Route path="/admin/foglalasok"             element={<Navigate to="/admin/leadek" replace />} />
                <Route path="/admin/leadek"                 element={<LeadCenterPage />} />
                <Route path="/admin/kuponok"                element={<CouponsAdminPage />} />
                <Route path="/admin/bejelentesek"           element={<StolenReportsAdminPage />} />
                <Route path="/admin/telefonvadasz"          element={<PhoneHunterAdminPage />} />

                <Route path="/telefon-szerviz-oroshaza"      element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/kijelzocsere-oroshaza"          element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/akkucsere-oroshaza"             element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/mobil-szerviz-oroshaza"         element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/telefon-javitas-oroshaza"       element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/hidrogel-folia-oroshaza"        element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />
                <Route path="/telefon-folia-oroshaza"         element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />
                {/* Márka-készletoldalak: ÖNÁLLÓ SEO-landingek, nem aliasok.
                    A /hasznalt-iphone-oroshaza korábban alias volt (canonical a
                    /telefonok-ra) – így nem tudott rangsorolni. Most saját
                    címmel, H1-gyel, tartalommal és önhivatkozó kanonikussal
                    fut. Tartalom: constants/markaOldalak.js. */}
                {MARKA_OLDALAK.map((m) => (
                  <Route key={m.slug} path={`/${m.slug}`} element={<PhonesPage marka={m} />} />
                ))}

                {/* Márka szerviz-hubok: iPhone / Samsung / Android (Xiaomi-Honor-
                    Huawei). Az első kettő korábban alias volt, canonical a
                    /szerviz-re – nem tudott rangsorolni. Tartalom:
                    constants/markaSzerviz.js */}
                {MARKA_SZERVIZ.map((m) => (
                  <Route key={m.slug} path={`/${m.slug}`} element={<MarkaSzervizPage adat={m} />} />
                ))}

                {/* Márka felvásárlási oldalak – CSAK iPhone és Samsung.
                    Honor/Huawei szándékosan nincs: a kalkulátor nem tudja
                    beárazni őket. Tartalom: constants/markaFelvasarlas.js */}
                {MARKA_FELVASARLAS.map((m) => (
                  <Route key={m.slug} path={`/${m.slug}`} element={<MarkaFelvasarlasPage adat={m} />} />
                ))}

                {/* ── Hidrogél specifikus SEO oldalak ──────────────────────── */}
                <Route path="/iphone-folia-oroshaza"              element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />
                <Route path="/samsung-folia-oroshaza"             element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />
                <Route path="/privacy-folia-oroshaza"             element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />
                <Route path="/onjavito-folia-oroshaza"            element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />
                <Route path="/telefon-kijelzovedo-folia-oroshaza" element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />
                <Route path="/uvegfolia-helyett-oroshaza"         element={<SEOAliasRoute><HydrogelPage /></SEOAliasRoute>} />

                {/* ── Probléma-intent SEO oldalak (emergency keresések) ──── */}
                <Route path="/iphone-nem-tolt-oroshaza"           element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/iphone-nem-kapcsol-be-oroshaza"     element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/betort-kijelzo-javitas-oroshaza"    element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/vizbe-esett-telefon-oroshaza"       element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/telefon-akkumulatora-gyorsan-lemerul-oroshaza" element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/iphone-kijelzo-csere-arak-oroshaza" element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/samsung-kijelzo-csere-arak-oroshaza" element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />
                <Route path="/telefon-javitas-azonnal-oroshaza"   element={<SEOAliasRoute><ServicePage /></SEOAliasRoute>} />

                {/* ── Felvásárlás probléma-intent SEO oldalak ─────────────── */}
                <Route path="/telefon-eladas-azonnal-oroshaza"    element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />
                <Route path="/iphone-eladas-oroshaza"             element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />
                <Route path="/samsung-eladas-oroshaza"            element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />
                <Route path="/hasznalt-telefon-felvasarlas-oroshaza" element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />
                <Route path="/ipad-eladas-oroshaza"               element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />
                <Route path="/apple-watch-eladas-oroshaza"        element={<SEOAliasRoute><BuybackPage /></SEOAliasRoute>} />

                {/* ── iPhone összehasonlító rendszer (adatvezérelt) ────────── */}
                <Route path="/osszehasonlitas"           element={<CompareIndexPage />} />
                <Route path="/osszehasonlitas/:pairSlug" element={<ComparePage />} />

                {/* Catch-all 404 – eddig az ismeretlen URL üres <main>-t adott */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </main>
          <Footer />
          <CookieBanner />
          <FloatingContactWidget />
          <MobileCTABar />
          <Suspense fallback={null}><EmailPopup /></Suspense>
        </BrowserRouter>
        </ActiveStoreProvider>
      </ContentProvider>
    </div>
  );
}

export default App;
